import React from 'react';
import { X } from 'lucide-react';
import { cn } from './cn';
import { Button, IconButton } from './Button';
import type { Tone } from './Badge';
import { useModalKeys } from '../../hooks/useModalKeys';

/**
 * Sobreposições do Atmos.
 *
 * Mantém a regra do painel (Fase 30b): o clique fora NÃO fecha — só o
 * botão ou o Esc — pra não perder formulário preenchido. Enter dispara a
 * confirmação quando o modal tem uma.
 *
 * Quando usar cada um, segundo o design system: Dialog para confirmação
 * destrutiva, Drawer para inspecionar um registro ao lado da lista,
 * Modal para um formulário focado.
 */

const WIDTH = {
  sm: 'w-[min(440px,100%)]',
  md: 'w-[min(640px,100%)]',
  lg: 'w-[min(880px,100%)]',
  xl: 'w-[min(1100px,100%)]',
} as const;

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  /** Ligado ao Enter. Deixe vazio em modal só de leitura. */
  onConfirm?: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  size?: keyof typeof WIDTH;
  footer?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  onConfirm,
  title,
  description,
  size = 'md',
  footer,
  className,
  children,
}: ModalProps) {
  if (!open) return null;
  return <ModalShell {...{ onClose, onConfirm, title, description, size, footer, className }}>{children}</ModalShell>;
}

/** Separado do Modal porque hook não pode ficar atrás de early return. */
function ModalShell({
  onClose,
  onConfirm,
  title,
  description,
  size = 'md',
  footer,
  className,
  children,
}: Omit<ModalProps, 'open'>) {
  useModalKeys(onClose, onConfirm);
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-[var(--scrim)] p-6">
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'flex max-h-[92vh] flex-col overflow-hidden rounded-[14px] border border-line bg-surface-2 shadow-xl animate-atmos-pop',
          WIDTH[size],
          className,
        )}
      >
        {title && (
          <div className="flex flex-none items-start justify-between gap-4 border-b border-line px-[22px] py-[18px]">
            <div className="min-w-0">
              <h2 className="text-base font-semibold tracking-[-0.02em] text-fg">{title}</h2>
              {description && <p className="mt-1 text-[13.5px] text-fg-2">{description}</p>}
            </div>
            <IconButton label="Fechar" variant="ghost" size="sm" onClick={onClose}>
              <X size={18} />
            </IconButton>
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto px-[22px] py-[18px]">{children}</div>
        {footer && (
          <div className="flex flex-none justify-end gap-2.5 border-t border-line px-[22px] py-[18px]">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Confirmação destrutiva — ícone no tom, duas ações, nada mais. */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  tone = 'danger',
  icon,
  loading,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: Extract<Tone, 'danger' | 'warning' | 'primary'>;
  icon?: React.ReactNode;
  loading?: boolean;
}) {
  if (!open) return null;
  return (
    <ConfirmShell
      {...{ onClose, onConfirm, title, description, confirmLabel, cancelLabel, tone, icon, loading }}
    />
  );
}

const CONFIRM_TONE = {
  danger: 'bg-danger-soft border-danger-line text-danger',
  warning: 'bg-warning-soft border-warning-line text-warning',
  primary: 'bg-primary-soft border-primary-line text-primary',
} as const;

function ConfirmShell({
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel,
  cancelLabel,
  tone,
  icon,
  loading,
}: {
  onClose: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  tone: 'danger' | 'warning' | 'primary';
  icon?: React.ReactNode;
  loading?: boolean;
}) {
  useModalKeys(onClose, onConfirm);
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-[var(--scrim)] p-6">
      <div
        role="dialog"
        aria-modal="true"
        className="w-[min(440px,100%)] rounded-[14px] border border-line bg-surface-2 shadow-xl animate-atmos-pop"
      >
        <div className="flex gap-3 px-[22px] pt-5">
          {icon && (
            <span
              className={cn(
                'grid h-[34px] w-[34px] flex-none place-items-center rounded-[9px] border',
                CONFIRM_TONE[tone],
              )}
            >
              {icon}
            </span>
          )}
          <div className="min-w-0">
            <h2 className="text-base font-semibold tracking-[-0.02em] text-fg">{title}</h2>
            {description && (
              <p className="mt-1.5 text-[13.5px] text-pretty text-fg-2">{description}</p>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2.5 px-[22px] py-5">
          <Button variant="secondary" onClick={onClose}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === 'danger' ? 'danger' : 'primary'}
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Painel lateral — para inspecionar um registro sem sair da lista. */
export function Drawer({
  open,
  onClose,
  title,
  description,
  footer,
  className,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  if (!open) return null;
  return <DrawerShell {...{ onClose, title, description, footer, className }}>{children}</DrawerShell>;
}

function DrawerShell({
  onClose,
  title,
  description,
  footer,
  className,
  children,
}: {
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}) {
  useModalKeys(onClose);
  return (
    <div className="fixed inset-0 z-[70] flex justify-end bg-[var(--scrim)]">
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'flex h-full w-[min(480px,100%)] flex-col border-l border-line bg-surface shadow-xl animate-atmos-in',
          className,
        )}
      >
        <div className="flex flex-none items-start justify-between gap-3 border-b border-line px-5 py-[18px]">
          <div className="min-w-0">
            {title && (
              <h2 className="text-[17px] font-semibold tracking-[-0.022em] text-fg">{title}</h2>
            )}
            {description && <p className="mt-0.5 text-[13px] text-fg-2">{description}</p>}
          </div>
          <IconButton label="Fechar" variant="ghost" size="sm" onClick={onClose}>
            <X size={18} />
          </IconButton>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-[18px]">{children}</div>
        {footer && (
          <div className="flex flex-none justify-end gap-2.5 border-t border-line px-5 py-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
